import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Client, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

import {
  bridgeAtom,
  cartAtom,
  chatIdAtom,
  errorAtom,
  isOpenChatAtom,
  isResetChatAtom,
  tokenAtom,
  useAtom,
  useSetValue,
  useValue,
} from '@/store';

import { AppEvents, getProductIdType } from '@/utils';

import { MESSAGE_LIMIT, RefreshErrorTypes, setCookie, useEvent } from '@/modules/common';
import {
  ErrorType,
  MessageReasonType,
  MessageSenderType,
  MessageType,
  ModelActionType,
  PageType,
} from '@/modules/models';
import { TOKEN_STORAGE } from '@/store/constants';

import { openChat } from '../api.service';
import { RECONNECT_TIMEOUT } from './constants';
import { useSessionFinishTime } from './sessionFinishTime.hook';
import { getSocketUrl } from './utils';

const MESSAGES_API = '/app/sendMessage';

export type ReturnType = {
  initConversation: () => void;
  sendMessage: (message: MessageType) => Promise<{ error: ErrorType } | void>;
  isSubscribed: boolean;
};

export type UseStompClientType = {
  onReset: () => void;
  onMessageReceived: (message: MessageType) => void;
  onHistoryReceived: (messages: MessageType[]) => void;
};

export const useStompClient = (props: UseStompClientType): ReturnType => {
  const propsRef = useRef(props);
  const isSendOpenChat = useRef(false);

  const setToken = useSetValue(tokenAtom);
  const [chatId, setChatId] = useAtom(chatIdAtom);
  const [isResetChat, setIsResetChat] = useAtom(isResetChatAtom);

  const [error, setError] = useAtom(errorAtom);
  const prevErrorRef = useRef(error);

  const isOpenChat = useValue(isOpenChatAtom);
  const { isOpen: isCartOpen, products } = useValue(cartAtom);
  const { productId, variantId, pageType, isAlwaysOpenWSConnection, isSupportSubDomainWithCookie } =
    useValue(bridgeAtom);

  const [isActiveTab, setIsActiveTab] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [stompClient, setStompClient] = useState<Client | null>(null);

  const onReset = useCallback(() => {
    propsRef.current.onReset();
    isSendOpenChat.current = false;

    setStompClient((prev) => {
      prev?.deactivate();
      return null;
    });

    setIsSubscribed(false);
  }, []);

  const setSessionFinishTime = useSessionFinishTime(onReset);

  const initConversation = useCallback(() => {
    if (!stompClient?.connected || (!isResetChat && !chatId)) {
      setError(ErrorType.EmptyChatId);
      return;
    }

    try {
      const { product_id: id, pageType: type } = getProductIdType({
        product_id: productId,
        pageType: isCartOpen ? PageType.CART : pageType,
      });

      stompClient.publish({
        destination: MESSAGES_API,
        body: JSON.stringify({
          content: '',
          product_id: id,
          variant_id: variantId,
          page_type: type,
          role: MessageSenderType.USER,
          reason: MessageReasonType.INITIALIZATION,
          clientTimestamp: Date.now(),
          cart: products.map(({ handle }) => handle),
        }),
      });
      setIsResetChat(false);
    } catch (e) {
      setError(ErrorType.NotInitialized);
    }
  }, [
    stompClient,
    chatId,
    setError,
    productId,
    isCartOpen,
    pageType,
    variantId,
    products,
    isResetChat,
    setIsResetChat,
  ]);

  const getSuggestion = useCallback(
    (e: CustomEvent<{ productsList: string[] }>) => {
      const { productsList } = e.detail;
      if (!stompClient?.connected) {
        return;
      }

      try {
        stompClient.publish({
          destination: MESSAGES_API,
          body: JSON.stringify({
            content: '',
            page_type: 'ADD_TO_CART_SUGGESTION',
            role: MessageSenderType.USER,
            reason: MessageReasonType.INITIALIZATION,
            clientTimestamp: Date.now(),
            cart: productsList,
          }),
        });
      } catch (e) {
        console.log(e);
      }
    },
    [stompClient]
  );

  const onHistory = useCallback((resp: IMessage) => {
    const messages = JSON.parse(resp.body) as MessageType[];

    // Filter out messages with the same content
    const uniqueMessagesMap = messages.reduce((map, message) => {
      if (message.id) {
        map.set(message.content, message);
      }
      return map;
    }, new Map<string, MessageType>());

    const filteredMessages = Array.from(uniqueMessagesMap.values());

    if (filteredMessages.length === 0) {
      return;
    }

    const latestTimestamp = filteredMessages.reduce((time, m) => {
      if (m?.action === ModelActionType.SAY && m.clientTimestamp && m.clientTimestamp > time) {
        return m.clientTimestamp;
      }

      return time;
    }, 0);

    const isLiveSession = setSessionFinishTime(latestTimestamp);

    if (isLiveSession) {
      propsRef.current.onHistoryReceived(filteredMessages);
      setIsSubscribed(true);
    } else {
      onReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTopic = useCallback((resp: IMessage) => {
    const message = JSON.parse(resp.body) as MessageType;
    propsRef.current.onMessageReceived(message);
  }, []);

  const connectToWebSocket = useCallback(
    async () => {
      setIsSubscribed(false);

      const url = getSocketUrl();

      let client: Client;
      const socket = new SockJS(url);

      const onConnect = (principal: string) => {
        client.subscribe('/app/register', (response) => {
          const data = JSON.parse(response.body);
          const token = data.content?.content;

          setChatId(data.chatId);
          setToken(token);

          if (isSupportSubDomainWithCookie && token) {
            setCookie({
              days: 7,
              value: token,
              name: TOKEN_STORAGE,
              useRootDomain: token,
            });
          }

          client.subscribe('/app/history', onHistory);
          client.subscribe('/topic/' + principal, onTopic);

          setIsSubscribed(true);
        });
      };

      const onClose = () => {
        client?.deactivate();
        setStompClient(null);
      };

      const onError = () => {
        // Reconnect only if the tab is active
        if (document.visibilityState === 'visible') {
          setTimeout(connectToWebSocket, RECONNECT_TIMEOUT);
        }
      };

      client = await new Promise((resolve) => {
        client = new Client({
          onStompError: onError,
          onWebSocketError: onError,
          onDisconnect: onClose,
          onWebSocketClose: onClose,
          webSocketFactory: () => socket,
          onConnect: (frame) => {
            const principal = frame.headers['user-name'];
            onConnect(principal);
            resolve(client);
          },
        });

        client.activate();
      });

      setSessionFinishTime();
      setStompClient(client);

      return client;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const sendMessage = useCallback(
    async (message: MessageType) => {
      setSessionFinishTime();

      if (message.content?.length > MESSAGE_LIMIT) {
        setError(ErrorType.MessageTooLong);
        return {
          error: ErrorType.MessageTooLong,
        };
      }

      let client = stompClient;

      if (!stompClient?.connected) {
        client = await connectToWebSocket();
      }

      if (!client?.connected) {
        setError(ErrorType.ConnectionFailure);
        return {
          error: ErrorType.ConnectionFailure,
        };
      }

      try {
        client.publish({
          destination: MESSAGES_API,
          body: JSON.stringify(message),
        });
      } catch (e) {
        setError(ErrorType.MessageNotDelivered);
        return {
          error: ErrorType.MessageNotDelivered,
        };
      }
    },
    [connectToWebSocket, setError, setSessionFinishTime, stompClient]
  );

  useEffect(() => {
    propsRef.current = props;
  }, [props]);

  useLayoutEffect(() => {
    const onVisibilityChange = () => {
      setIsActiveTab(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const onOpenChat = async () => {
      try {
        await openChat(productId);
        isSendOpenChat.current = true;
      } catch (e) {
        console.error('Error in sendOpenedChatRequest:', e);
      }
    };

    if (!isSendOpenChat.current && isOpenChat && isSubscribed) {
      onOpenChat();
    }
  }, [isOpenChat, isSubscribed, productId]);

  useEffect(() => {
    if ((isAlwaysOpenWSConnection || isOpenChat) && isActiveTab && !stompClient) {
      connectToWebSocket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenChat, stompClient, isActiveTab, isAlwaysOpenWSConnection]);

  useEffect(() => {
    if (prevErrorRef.current && !error) {
      if (RefreshErrorTypes.includes(prevErrorRef.current)) {
        setStompClient((prev) => {
          prev?.deactivate();
          return null;
        });
      }
    }

    prevErrorRef.current = error;
  }, [error]);

  useEvent({ name: AppEvents.GET_SUGGESTION, handler: getSuggestion });

  return { sendMessage, initConversation, isSubscribed };
};
